import React, { useEffect, useState } from 'react';
import { Info, Repeat } from 'lucide-react';
import { ResolvedPeriod } from '../PeriodPicker';
import { FlakyStoryRow, QualityApi } from './qualityApi';

const JIRA_BROWSE = 'https://hrsinternational.atlassian.net/browse/';

interface FlakyStoriesTableProps {
  resolvedPeriod: ResolvedPeriod | null;
}

export const FlakyStoriesTable: React.FC<FlakyStoriesTableProps> = ({ resolvedPeriod }) => {
  const [rows, setRows] = useState<FlakyStoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await QualityApi.getFlakyStories({ period: null, resolvedPeriod });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err instanceof Error ? err.message : 'Failed to load flaky stories');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Repeat size={18} />
          Flaky stories
          <span
            className="stat-hint-icon"
            title="Stories that failed verification more than once within the Period. Often indicates weak acceptance criteria, flaky infrastructure, or a complex module — good candidates for a retro."
            aria-label="About flaky stories"
          >
            <Info size={14} />
          </span>
        </h3>
        <p>Stories with ≥ 2 Verification Failed transitions during the Period.</p>
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : rows.length === 0 ? (
        <p className="quality-breakdown__empty">
          Nothing flaky yet — every story in this Period passed QA on the first (or second) try.
        </p>
      ) : (
        <table className="estimation-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Summary</th>
              <th>VF count</th>
              <th>Components</th>
              <th>Assignee</th>
              <th>QA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="estimation-task-key">
                  <a
                    href={`${JIRA_BROWSE}${encodeURIComponent(r.key)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="task-link"
                  >
                    {r.key}
                  </a>
                </td>
                <td className="estimation-task-summary">{r.summary}</td>
                <td>{r.vfCount}</td>
                <td>{(r.components || []).join(', ') || '—'}</td>
                <td>{r.assignee || '—'}</td>
                <td>{r.qa || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
